import type {
  AwsMediaConnectFlow,
  AwsMediaConnectOutput,
  AwsMediaLiveChannel,
  AwsMediaLiveInput,
  AwsMediaLiveInputDevice,
  AwsMediaSummary,
} from "@/hooks/useAwsMedia";
import type { Tables } from "@/integrations/neon/types";

export type ProjectAwsResourceLink = Tables<"project_aws_resource_links">;
export type AwsResourceType = ProjectAwsResourceLink["resource_type"];

export type AwsResourceCandidate = {
  type: AwsResourceType;
  id?: string | null;
  arn?: string | null;
  name?: string | null;
  status?: string | null;
  resource:
    | AwsMediaConnectFlow
    | AwsMediaConnectOutput
    | AwsMediaLiveChannel
    | AwsMediaLiveInput
    | AwsMediaLiveInputDevice;
};

export type ResolvedAwsResourceLink = {
  link: ProjectAwsResourceLink;
  resource: AwsResourceCandidate | null;
};

export function awsResourceKey(input: {
  resource_type?: AwsResourceType;
  type?: AwsResourceType;
  resource_arn?: string | null;
  arn?: string | null;
  resource_id?: string | null;
  id?: string | null;
}) {
  const type = input.resource_type ?? input.type;
  const identity =
    input.resource_arn ?? input.arn ?? input.resource_id ?? input.id ?? "";
  return type ? `${type}:${identity}` : identity;
}

export function awsSummaryCandidates(
  summary: AwsMediaSummary | undefined,
): AwsResourceCandidate[] {
  if (!summary) return [];

  return [
    ...summary.mediaConnect.flows.flatMap((flow) => [
      {
        type: "mediaconnect_flow" as const,
        id: flow.arn,
        arn: flow.arn,
        name: flow.name,
        status: flow.status,
        resource: flow,
      },
      ...flow.outputs.map((output) => ({
        type: "mediaconnect_output" as const,
        id: output.arn,
        arn: output.arn,
        name: output.name,
        status: output.outputStatus,
        resource: output,
      })),
    ]),
    ...summary.mediaLive.channels.map((channel) => ({
      type: "medialive_channel" as const,
      id: channel.id,
      arn: channel.arn,
      name: channel.name,
      status: channel.state,
      resource: channel,
    })),
    ...summary.mediaLive.inputs.map((input) => ({
      type: "medialive_input" as const,
      id: input.id,
      arn: input.arn,
      name: input.name,
      status: input.state,
      resource: input,
    })),
    ...summary.mediaLive.inputDevices.map((device) => ({
      type: "medialive_input_device" as const,
      id: device.id,
      arn: device.arn,
      name: device.name,
      status: device.connectionState,
      resource: device,
    })),
  ];
}

export function findAwsResourceForLink(
  link: ProjectAwsResourceLink,
  candidates: AwsResourceCandidate[],
) {
  return (
    candidates.find(
      (candidate) =>
        candidate.type === link.resource_type &&
        ((link.resource_arn && candidate.arn === link.resource_arn) ||
          (link.resource_id && candidate.id === link.resource_id)),
    ) ?? null
  );
}

export function resolveAwsResourceLinks(
  links: ProjectAwsResourceLink[],
  summary: AwsMediaSummary | undefined,
): ResolvedAwsResourceLink[] {
  const candidates = awsSummaryCandidates(summary);
  return links.map((link) => ({
    link,
    resource: findAwsResourceForLink(link, candidates),
  }));
}

export function awsResourceTypeLabel(type: AwsResourceType) {
  switch (type) {
    case "mediaconnect_flow":
      return "MediaConnect Flow";
    case "mediaconnect_output":
      return "MediaConnect Output";
    case "medialive_channel":
      return "MediaLive Channel";
    case "medialive_input":
      return "MediaLive Input";
    case "medialive_input_device":
      return "Elemental Link";
  }
}
